<template>
  <section class="bg-gold-primary-10 pb-12 pt-20">
    <div class="container mx-auto max-w-3xl">
      <h2 class="mb-8 text-[26px] font-bold tracking-tight text-black-80 tablet:text-[32px]">
        {{ t('landing.faq.title') }}
      </h2>
      <div
        v-for="group in groups"
        :key="group.id"
        class="mb-8"
      >
        <h3
          v-if="group.id"
          class="mb-4 text-lg font-bold text-gold-primary-120 tablet:text-xl"
        >
          {{ t(`${groupPrefix}${group.id}`) }}
        </h3>
        <div class="flex flex-col gap-2">
          <details
            v-for="item in group.items"
            :key="item.id"
            class="group rounded-xl bg-white p-3"
            @toggle="onToggle($event, item.id)"
          >
            <summary
              class="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-black-100 tablet:text-base"
            >
              <span>{{ t(`${FAQ_PREFIX}${item.id}Q`, params) }}</span>
              <svg
                viewBox="0 0 24 24"
                class="size-5 shrink-0 text-gold-primary-120 transition-transform group-open:rotate-45"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path
                  d="M12 5v14M5 12h14"
                  stroke-linecap="round"
                />
              </svg>
            </summary>
            <div class="mt-3 space-y-2 text-sm text-black-80">
              <i18n-t
                v-for="para in item.answer"
                :key="para.key"
                :keypath="`${FAQ_PREFIX}${para.key}`"
                scope="global"
                tag="p"
              >
                <template
                  v-for="(value, slotName) in slotValues(para)"
                  :key="`v-${slotName}`"
                  #[slotName]
                  ><span>{{ value }}</span></template
                >
                <template
                  v-for="(labelKey, slotName) in para.bold"
                  :key="slotName"
                  #[slotName]
                >
                  <strong>{{ t(`${FAQ_PREFIX}${labelKey}`) }}</strong>
                </template>
                <template
                  v-if="para.link"
                  #[para.link.slot]
                >
                  <a
                    :href="para.link.href"
                    class="font-semibold text-gold-primary-120 underline underline-offset-2"
                    >{{ t(`${FAQ_PREFIX}${para.link.labelKey}`) }}</a
                  >
                </template>
              </i18n-t>
            </div>
          </details>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { FaqGroup, FaqItem, FaqParagraph } from '~/utils/landing/faq';
import { faqLead, faqPara } from '~/utils/landing/faq';

// Keys live under `landing.faqItems` (or `prefix`): `<id>Q` is the question, each answer
// paragraph is `<key>`; `bold` maps a slot to the key of its <strong> text, `values` fills
// slots with plain text, `link` renders one slot as a link.
const {
  groups: groupsProp,
  prefix = 'landing.faqItems.',
  groupPrefix = 'landing.faq.groups.',
  params = {},
} = defineProps<{
  groups?: FaqGroup[];
  prefix?: string;
  groupPrefix?: string;
  /** Plain-text values for `{name}` placeholders in every question and answer. */
  params?: Record<string, string>;
}>();

const emit = defineEmits<{
  (e: 'opened', index: number): void;
}>();

const FAQ_PREFIX = prefix;

const { t } = useI18n();
const { formatNumber } = useFormat();
const { prices } = useLandingFacts();

const usd = (amount: number) => `$${formatNumber(amount)}`;

const costParagraph = computed<FaqParagraph>(() => {
  const plans = { member: 'planMember', pro: 'planPro', team: 'planTeam' };
  if (!prices.value) return faqPara('costA1Fallback', plans);
  return {
    ...faqPara('costA1', plans),
    values: {
      memberPrice: usd(prices.value.memberAnnual),
      proPrice: usd(prices.value.proAnnual),
      teamPrice: usd(prices.value.teamAnnual),
    },
  };
});

const internationalGroups = computed<FaqGroup[]>(() => [
  {
    id: 'eligibility',
    items: [
      {
        id: 'rera',
        answer: [faqPara('reraA1'), faqLead('reraA3')],
      },
      { id: 'parallel', answer: [faqPara('parallelA1')] },
      { id: 'noDubai', answer: [faqPara('noDubaiA1')] },
      {
        id: 'abroad',
        answer: [
          faqPara('abroadA1'),
          faqPara('abroadA2'),
          faqLead('abroadA3'),
          faqLead('abroadA4'),
          faqLead('abroadA5'),
          faqLead('abroadA6'),
        ],
      },
    ],
  },
  {
    id: 'earnings',
    items: [
      { id: 'paid', answer: [faqPara('paidA1ProTeam')] },
      { id: 'deductions', answer: [faqPara('deductionsA1')] },
      { id: 'tiers', answer: [faqPara('tiersA1')] },
      { id: 'cancel', answer: [faqLead('cancelA1'), faqLead('cancelA2'), faqPara('cancelA3')] },
      { id: 'resales', answer: [faqPara('resalesA1')] },
    ],
  },
  {
    id: 'platform',
    items: [
      {
        id: 'cost',
        answer: [costParagraph.value, faqPara('costFastPayProTeam')],
      },
      { id: 'catalogue', answer: [faqPara('catalogueA1')] },
      { id: 'vault', answer: [faqPara('vaultA1')] },
      { id: 'mobile', answer: [faqPara('mobileA1'), faqPara('mobileA2')] },
    ],
  },
  {
    id: 'switching',
    items: [
      { id: 'poach', answer: [faqPara('poachA1')] },
      { id: 'protection', answer: [faqPara('protectionA1')] },
      { id: 'privacy', answer: [faqPara('privacyA1')] },
    ],
  },
]);

const groups = computed(() => groupsProp ?? internationalGroups.value);

const slotValues = (para: FaqParagraph): Record<string, string> => ({ ...params, ...para.values });

const itemIds = computed(() => groups.value.flatMap((group) => group.items.map((item) => item.id)));
const onToggle = (event: Event, id: string) => {
  if ((event.target as HTMLDetailsElement).open) emit('opened', itemIds.value.indexOf(id) + 1);
};

// Plain-text answer for Google's FAQPage schema: each paragraph with its bold
// slots interpolated as text, paragraphs joined by a space.
const answerText = (item: FaqItem): string =>
  item.answer
    .map((p) => {
      const values = {
        ...params,
        ...p.values,
        ...Object.fromEntries(
          Object.entries(p.bold ?? {}).map(([slot, labelKey]) => [slot, t(`${FAQ_PREFIX}${labelKey}`)]),
        ),
        ...(p.link ? { [p.link.slot]: t(`${FAQ_PREFIX}${p.link.labelKey}`) } : {}),
      };
      return t(`${FAQ_PREFIX}${p.key}`, values);
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

// FAQPage rich-result schema. Flattens all groups into one array since
// FAQPage doesn't have a "section" concept.
const faqJsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: groups.value
    .flatMap((g) => g.items)
    .map((item) => ({
      '@type': 'Question',
      name: t(`${FAQ_PREFIX}${item.id}Q`, params),
      acceptedAnswer: {
        '@type': 'Answer',
        text: answerText(item),
      },
    })),
}));

useHead({
  script: computed(() => [
    {
      key: 'schema-org-faqpage-agents',
      type: 'application/ld+json',
      // `<` → \u003c: translated copy can never close the <script> element early; the JSON
      // parses to the same values.
      innerHTML: JSON.stringify(faqJsonLd.value).replace(/</g, '\\u003c'),
    },
  ]),
});
</script>
